/* eslint-disable react/jsx-props-no-spreading */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
	FormGroup, Col, ControlLabel, FormControl, Checkbox, Radio,
} from 'react-bootstrap';
import { Select } from '@components/common';
import MaskedInput from 'react-text-mask';
import DateTimePicker from 'react-datetime';
import { v4 as uuid } from 'uuid';
import {
	isEqual, toString, cloneDeep, set,
} from 'lodash';

class FormField extends Component {
	constructor(props) {
		super(props);

		this.controlId = uuid();
		this.debounceTimeout = null;
		this.debounceCallback = null;

		this.state = {
			controlledValue : props.value,

			prevProps : {
				value : props.value,
			},
		};
	}

	static getDerivedStateFromProps(nextProps, lastState) {
		const prevProps = lastState.prevProps || {};

		const controlledValue = prevProps.value !== nextProps.value
			? nextProps.value : lastState.controlledValue;

		return {
			prevProps : nextProps,
			controlledValue,
		};
	}

	shouldComponentUpdate = ({
		label,
		fieldStyle : {
			fieldCol,
			labelCol,
			labelClass,
			groupClass,
		},
		addOnCol,
		options,
		disabled,
		loading,
		name,
		placeholder,
		dateFormat,
		timeFormat,
		controlType,
		mask,
		validation,
	}, { controlledValue }) => {
		const lastProps = this.props;
		const lastState = this.state;

		return (
			!isEqual(label, lastProps.label)
			|| !isEqual(labelCol, lastProps.fieldStyle.labelCol)
			|| !isEqual(fieldCol, lastProps.fieldStyle.fieldCol)
			|| !isEqual(addOnCol, lastProps.addOnCol)
			|| !isEqual(options, lastProps.options)
			|| !isEqual(labelClass, lastProps.fieldStyle.labelClass)
			|| !isEqual(groupClass, lastProps.fieldStyle.groupClass)
			|| !isEqual(disabled, lastProps.disabled)
			|| !isEqual(loading, lastProps.loading)
			|| !isEqual(name, lastProps.name)
			|| !isEqual(placeholder, lastProps.placeholder)
			|| !isEqual(dateFormat, lastProps.dateFormat)
			|| !isEqual(timeFormat, lastProps.timeFormat)
			|| !isEqual(controlType, lastProps.controlType)
			|| !isEqual(mask, lastProps.mask)
			|| !isEqual(validation, lastProps.validation)
			|| !isEqual(controlledValue, lastState.controlledValue)
		);
	}

	changeHandler = (event) => {
		let value;
		let result;
		const { controlType, onChange } = this.props;

		switch (controlType) {
			default:
				value = event.target.value;
				result = { target : { value } };
				break;

			case 'select':
				value = event ? event.value : '';
				result = event ? { value } : '';
				break;

			case 'checkbox':
				value = event.target.checked;
				result = { target : { checked : value } };
				break;

			case 'date':
				value = event;
				result = value;
				break;

			case 'file':
				value = event.target.files;
				result = value;
				break;
		}

		this.setState({ controlledValue : value });

		if (controlType === 'default' || controlType === 'masked') {
			/**
			 * Clear debounceTimeout to prevent updating the Store
			 * multiple times using the JS native function
			 * clearTimeout
			 */
			if (this.debounceTimeout !== null) clearTimeout(this.debounceTimeout);

			this.debounceCallback = () => onChange(result);

			// Define a timeout to update save the field into Store
			this.debounceTimeout = setTimeout(this.debounceCallback, 300);
		} else onChange(result);
	}

	onChangeTime = (time) => (event) => {
		const newState = cloneDeep(this.state);

		set(newState, ['controlledValue', time], event);

		this.setState(newState);
	}

	onBlur = () => {
		const { onChange } = this.props;
		const { controlledValue } = this.state;
		onChange(controlledValue);
	}

	render() {
		const {
			validation,
			label,
			fieldStyle : {
				fieldCol,
				labelCol,
				labelClass,
				groupClass,
			},
			addOnCol,
			disabled,
			loading,
			name,
			placeholder,
			dateFormat,
			timeFormat,
			controlType,
			mask,
			options,
			columnClass,
			addOn,
			maxLength,
			isClearable,
			onKeyPress,
		} = this.props;

		const { controlledValue : value } = this.state;

		const controlProps = {
			name,
			value,
			disabled,
			placeholder,
			onChange : this.changeHandler,
			maxLength,
		};

		let TargetComponent = FormControl;

		switch (controlType) {
			default:
			case 'textarea':
				TargetComponent = FormControl;

				if (controlType === 'textarea') controlProps.componentClass = 'textarea';
				else controlProps.onKeyPress = onKeyPress;
				break;
			case 'file':
				TargetComponent = FormControl;
				controlProps.type = 'file';
				break;
			case 'masked':
				TargetComponent = MaskedInput;

				// Set specific control props
				controlProps.className = 'form-control';
				controlProps.mask = mask;
				controlProps.guide = false;
				controlProps.onKeyPress = onKeyPress;
				break;

			case 'select':
				TargetComponent = Select;

				// Set specific control props
				controlProps.options = options;
				controlProps.isLoading = loading;
				controlProps.isClearable = isClearable;
				break;

			case 'checkbox':
				TargetComponent = Checkbox;

				// Set specific control props
				controlProps.checked = value;

				/**
				 * For checkbox delete the
				 * unnecessary properties
				 */
				delete controlProps.value;
				delete controlProps.placeholder;
				break;

			case 'radio':
				TargetComponent = Radio;

				// Set specific control props
				controlProps.className = 'margin-right-15';

				/**
				 * For radio delete the
				 * unnecessary properties
				 */
				delete controlProps.value;
				delete controlProps.placeholder;
				break;

			case 'dateRange':
				controlProps.dateFormat = dateFormat;
				controlProps.timeFormat = timeFormat;

				/**
				 * For dateRange delete the
				 * unnecessary properties
				 */
				delete controlProps.value;
				delete controlProps.placeholder;
				delete controlProps.onChange;
				break;
		}

		return (
			<FormGroup
				controlId={this.controlId}
				validationState={validation}
				className={groupClass}
			>
				<If condition={label}>
					<Col
						componentClass={ControlLabel}
						sm={labelCol}
						className={labelClass}
					>
						{label}
					</Col>
				</If>
				<Choose>
					<When condition={controlType === 'radio'}>
						<Col
							sm={fieldCol}
							className={columnClass}
						>
							<div className="flex flex-align-items-center flex-wrap">
								<For
									each="option"
									index="index"
									of={options}
								>
									<TargetComponent
										key={`radio_${this.controlId}_${index}`}
										{...controlProps}
										value={option.value}
										checked={toString(option.value) === toString(value)}
									>
										{option.label}
									</TargetComponent>
								</For>
							</div>
						</Col>
					</When>
					<When condition={controlType === 'dateRange'}>
						<Col
							sm={Math.floor(fieldCol / 2)}
							className={columnClass}
						>
							<ControlLabel>Start</ControlLabel>
							<DateTimePicker
								{...controlProps}
								value={value.start}
								onChange={this.onChangeTime('start')}
								onBlur={this.onBlur}
							/>
						</Col>
						<Col
							sm={Math.floor(fieldCol / 2)}
							className={columnClass}
						>
							<ControlLabel>End</ControlLabel>
							<DateTimePicker
								{...controlProps}
								value={value.end}
								onChange={this.onChangeTime('end')}
								onBlur={this.onBlur}
							/>
						</Col>
					</When>
					<Otherwise>
						<Col
							sm={fieldCol}
							className={columnClass}
						>
							<TargetComponent {...controlProps} />
						</Col>
					</Otherwise>
				</Choose>
				<If condition={!!addOn}>
					<Col sm={addOnCol}>
						{addOn}
					</Col>
				</If>
			</FormGroup>
		);
	}
}

FormField.propTypes = {
	label       : PropTypes.string,
	value       : PropTypes.any.isRequired,
	onChange    : PropTypes.func.isRequired,
	name        : PropTypes.string.isRequired,
	placeholder : PropTypes.string,
	addOnCol    : PropTypes.number,
	options     : PropTypes.array,
	columnClass : PropTypes.string,
	disabled    : PropTypes.bool,
	loading     : PropTypes.bool,
	addOn       : PropTypes.any,
	maxLength   : PropTypes.number,
	isClearable : PropTypes.bool,
	controlType : PropTypes.oneOf([
		'default',
		'masked',
		'select',
		'checkbox',
		'textarea',
		'radio',
		'dateRange',
		'file',
	]),
	mask : PropTypes.oneOfType([
		PropTypes.array,
		PropTypes.func,
	]),
	validation : PropTypes.oneOf([
		'success',
		'warning',
		'error',
		null,
	]),
	dateFormat : PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.bool,
	]),
	timeFormat : PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.bool,
	]),
	fieldStyle : PropTypes.shape({
		fieldCol   : PropTypes.number,
		labelCol   : PropTypes.number,
		labelClass : PropTypes.string,
		groupClass : PropTypes.string,
	}),
	onKeyPress : PropTypes.func,
};

FormField.defaultProps = {
	label       : null,
	addOnCol    : 1,
	validation  : null,
	disabled    : false,
	loading     : false,
	placeholder : '',
	dateFormat  : 'MM/DD/YYYY',
	timeFormat  : 'hh:mm A',
	addOn       : undefined,
	controlType : 'default',
	mask        : [],
	options     : [],
	columnClass : '',
	isClearable : true,
	maxLength   : undefined,
	fieldStyle  : {
		fieldCol   : 7,
		labelCol   : 3,
		labelClass : '',
		groupClass : '',
	},
	onKeyPress : () => {},
};

export default FormField;
